import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
  AnchorHTMLAttributes,
  MouseEvent,
} from 'react';

interface RouterContextValue {
  currentPath: string; // e.g. "/student/trips"
  fullPath: string; // e.g. "/student/trips?universityId=uni_cairo"
  queryParams: Record<string, string>;
  navigate: (path: string, options?: { replace?: boolean }) => void;
  matchRoute: (pattern: string) => { isMatch: boolean; params: Record<string, string> };
}

const parseQueryParams = (search: string): Record<string, string> => {
  const params: Record<string, string> = {};
  if (!search) return params;
  const cleaned = search.startsWith('?') ? search.slice(1) : search;
  const pairs = cleaned.split('&');
  for (const pair of pairs) {
    if (!pair) continue;
    const [k, v] = pair.split('=');
    if (k) {
      params[decodeURIComponent(k)] = v ? decodeURIComponent(v) : '';
    }
  }
  return params;
};

const RouterContext = createContext<RouterContextValue>({
  currentPath: window.location.pathname || '/',
  fullPath: window.location.pathname + window.location.search,
  queryParams: {},
  navigate: () => {},
  matchRoute: () => ({ isMatch: false, params: {} }),
});

export interface RouterProviderProps {
  children: ReactNode;
}

/**
 * Lightweight client-side SPA router utilizing standard HTML5 History API.
 * Supports pattern matching (/path/:param), query parameters, browser Back/Forward,
 * and seamless transitions for Hostinger .htaccess deployment.
 */
export const RouterProvider: React.FC<RouterProviderProps> = ({ children }) => {
  const [fullPath, setFullPath] = useState<string>(
    (window.location.pathname || '/') + (window.location.search || '')
  );

  useEffect(() => {
    const handlePopState = () => {
      setFullPath((window.location.pathname || '/') + (window.location.search || ''));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = useCallback((path: string, options?: { replace?: boolean }) => {
    if (options?.replace) {
      window.history.replaceState({}, '', path);
    } else {
      window.history.pushState({}, '', path);
    }
    setFullPath(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const currentPath = useMemo(() => {
    const [path] = fullPath.split('?');
    return path || '/';
  }, [fullPath]);

  const queryParams = useMemo(() => {
    const [, search] = fullPath.split('?');
    return parseQueryParams(search || '');
  }, [fullPath]);

  const matchRoute = useCallback(
    (pattern: string): { isMatch: boolean; params: Record<string, string> } => {
      const patternParts = pattern.split('/').filter(Boolean);
      const currentParts = currentPath.split('/').filter(Boolean);

      if (patternParts.length !== currentParts.length) {
        return { isMatch: false, params: {} };
      }

      const params: Record<string, string> = {};
      for (let i = 0; i < patternParts.length; i++) {
        const pPart = patternParts[i];
        const cPart = currentParts[i];

        if (pPart.startsWith(':')) {
          const paramName = pPart.slice(1);
          params[paramName] = cPart;
        } else if (pPart !== cPart) {
          return { isMatch: false, params: {} };
        }
      }

      return { isMatch: true, params };
    },
    [currentPath]
  );

  return (
    <RouterContext.Provider
      value={{
        currentPath,
        fullPath,
        queryParams,
        navigate,
        matchRoute,
      }}
    >
      {children}
    </RouterContext.Provider>
  );
};

export const useRouter = (): RouterContextValue => {
  return useContext(RouterContext);
};

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  replace?: boolean;
  children: ReactNode;
}

export const Link: React.FC<LinkProps> = ({
  to,
  replace = false,
  children,
  onClick,
  className,
  ...rest
}) => {
  const { navigate } = useRouter();

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (onClick) onClick(e);
    if (!e.defaultPrevented && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      navigate(to, { replace });
    }
  };

  return (
    <a href={to} onClick={handleClick} className={className} {...rest}>
      {children}
    </a>
  );
};
